/**
 * Design-contract constants for the consent sheet — see ADR-0065
 * "Equal-weight design contract (GDPR equivalence)".
 *
 * Co-located with the sheet but extracted into a pure module so they
 * can be unit-tested without rendering the Tamagui tree. Imported by
 * `ConsentSheet.tsx` so the contract IS the constant — if a future
 * refactor flips a default ON, the corresponding test fails.
 */

import type { ConsentDecision } from '@my-fullstack-app/itinerary-types';

/**
 * Initial state for the per-purpose toggle row. Both purposes default
 * to OFF — opt-in posture. On first launch the user sees every toggle
 * off; in review mode the toggles seed to the user's current consent
 * before this default applies.
 *
 * DO NOT change without revising ADR-0065 and the associated
 * `consent-sheet-flow.test.ts` assertions.
 */
export const INITIAL_TOGGLE_DEFAULTS = {
  analytics: false,
  affiliates: false,
} as const;

/**
 * Decision payload produced by the footer's "Accept all" CTA. Stamping
 * (decisionAt / version) happens inside `useConsentStore.setConsent`.
 */
export const ACCEPT_ALL_DECISION = {
  analytics: true,
  affiliates: true,
} as const;

/**
 * Decision payload produced by the footer's "Reject all" CTA. Independent
 * of the toggle draft: tapping Reject all commits all-false regardless
 * of what the user has flipped, which is the one-tap withdrawal path
 * GDPR Art. 7(3) requires to mirror the one-tap accept path.
 */
export const REJECT_ALL_DECISION = {
  analytics: false,
  affiliates: false,
} as const;

/**
 * Working draft for the two toggles. Exported so tests can build the
 * same record the component does without dragging Tamagui in.
 */
export type ConsentDraft = Pick<
  ConsentDecision,
  'analytics' | 'affiliates'
>;

/**
 * Compose a draft from individual toggle states. Identity-shaped, but
 * keeping it as a named function leaves a future surface to layer in
 * cross-toggle invariants (e.g. "affiliates requires analytics") if
 * the policy ever demands them. Used by the inline "Save preferences"
 * link to commit whatever the toggles currently say.
 */
export function buildDraftFromToggles(state: ConsentDraft): ConsentDraft {
  return {
    analytics: state.analytics,
    affiliates: state.affiliates,
  };
}
